import { VariableDefinition, getUnitSuffix } from '../variable-schema';

export function formatValueForScss(value: string | number, def: VariableDefinition): string {
  const suffix = getUnitSuffix(def.type);
  if (suffix) return `${value}${suffix}`;
  return String(value);
}

export function sanitizeColorValue(value: string): string {
  // `/` is a separator in modern colour syntax (`hsl(120deg 50% 50% / 0.5)`), so it is kept
  // and only the comment delimiters it can form are removed.
  return (value ?? '').replace(/\/\*|\*\//g, '').replace(/[^#0-9a-zA-Z(),.%/\s-]/g, '');
}

export interface LintResult {
  output: string;
  balanced: boolean;
  commentClosed: boolean;
}

const commentDepthAfter = (line: string, open: boolean): boolean => {
  let rest = line;
  let isOpen = open;

  while (rest) {
    const marker = rest.indexOf(isOpen ? '*/' : '/*');
    if (marker < 0) break;
    rest = rest.slice(marker + 2);
    isOpen = !isOpen;
  }

  return isOpen;
};

export function lintCustomCss(input: string): LintResult {
  const lines = input.split('\n').map((l) => l.trim());
  const output: string[] = [];
  let depth = 0;
  let prevBlank = false;
  let commentOpen = false;

  for (const line of lines) {
    commentOpen = commentDepthAfter(line, commentOpen);
    if (line === '') {
      if (!prevBlank && output.length > 0) output.push('');
      prevBlank = true;
      continue;
    }
    prevBlank = false;

    const opens = (line.match(/{/g) || []).length;
    const closes = (line.match(/}/g) || []).length;
    const startsWithClose = line.startsWith('}');
    const indentDepth = startsWithClose ? Math.max(depth - 1, 0) : depth;

    output.push('  '.repeat(indentDepth) + line);
    depth += opens - closes;
  }

  while (output.length > 0 && output[output.length - 1] === '') output.pop();

  return { output: output.join('\n'), balanced: depth === 0, commentClosed: !commentOpen };
}

export function wrapDisplayForVisibilityToggle(input: string, varName: string | undefined): string {
  if (!varName) return input;
  const lines = input.split('\n');
  const output: string[] = [];
  let depth = 0;

  for (const line of lines) {
    const opens = (line.match(/{/g) || []).length;
    const closes = (line.match(/}/g) || []).length;

    if (depth === 0) {
      const m = line.match(/^(\s*)display\s*:\s*(.+?);(\s*)$/);
      if (m) {
        const [, leading, rawValue, trailing] = m;
        const value = rawValue.trim();
        const isSafe = !value.startsWith('var(--xrpShow') && value !== 'none' && !/!important/.test(value) && !/[$#]/.test(value);
        if (isSafe) {
          output.push(`${leading}display: var(${varName}, ${value});${trailing}`);
          depth += opens - closes;
          continue;
        }
      }
    }

    output.push(line);
    depth += opens - closes;
  }

  return output.join('\n');
}
